import React, {useEffect, useState} from 'react';
import {Formik} from 'formik';
import {useRouteMatch} from 'react-router-dom';
import Form_4View from './Form_4-view';
import {checkRequiredValues} from 'common/utils/form/checkRequiredValues';
import {normalizeFormValues} from 'common/utils/form/normalizeFormValues';
import {pathnames as P} from 'common/constants/pathnames';
import {fileTypeId as T} from '../../constants/fileTypeId';
import {isFileUploaded} from '../../utils/isFileUploaded';
import SilentSubmitHandler from '../SilentSubmitHandler';
import ModalPreviewDocument from '../../../../common/components/ModalPreviewDocument';
import {
  buildTypeOptions,
  buildPeriodOptions,
  facingOptions,
  streetLocationOptions,
  streetTypeOptions,
  streetTrafficOptions,
  parkingIssuesTimesOptions,
  landSurroundingsOptions,
  landShapeOptions
} from '../../../../common/constants/options';
import {uploadingProgress} from '../../../../common/utils/form/uploadingProgress';
import {
  filterFileListByFileTypeId,
  getSharedAndPrivateFiles,
  getFileFolderID
} from '../../utils/fileHelpers';
import ButtonSubmitForm from '../ButtonSubmitForm';
import SpinnerContainer from '../../../../common/components/SpinnerContainer';
import {getOption} from "../../utils/getOption";

const Form_4 = ({
                  submitStep,
                  stepData,
                  uploadFile,
                  getShumaStepStatus,
                  showGlobalErrorMessage,
                  getStepData,
                  getFileList,
                  filesListData
                }) => {
  const [reinit, setReinit] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showUploadingFilePopUp, setShowUploadingFilePopUp] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    fileList,
    streetName,
    buildType,
    buildPeriod,
    facing,
    streetLocation,
    streetType,
    streetTraffic,
    parkingIssues,
    publicTransport,
    parkingIssuesTimes,
    adjacentStreets,
    neighborhoodName,
    landSurroundings,
    landSurroundingsText,
    landShape,
    ...rest
  } = stepData;

  const landSurroundingsArray = landSurroundings ? landSurroundings.split(',') : [];
  const previousLandSurroundings = landSurroundings ? landSurroundingsOptions.filter(opt => landSurroundingsArray.includes(`${opt.value}`)) : [];

  const initialValues = {
    street: streetName || '',
    buildType: getOption(buildTypeOptions, buildType),
    buildPeriod: getOption(buildPeriodOptions, buildPeriod),
    facing: getOption(facingOptions, facing),
    streetLocation: getOption(streetLocationOptions, streetLocation),
    streetType: getOption(streetTypeOptions, streetType),
    streetTraffic: getOption(streetTrafficOptions, streetTraffic),
    parkingIssues: Boolean(parkingIssues) || null,
    publicTransport: Boolean(publicTransport) || null,
    parkingIssuesTimes: getOption(parkingIssuesTimesOptions, parkingIssuesTimes),
    areaLimits: '',
    areaUniqueFeatures: '',
    envDevelopmentLevel: '',
    streetMainUsage: '',
    adjacentStreets: adjacentStreets || '',
    neighborhoodName: neighborhoodName || '',
    propertyLocationDesc: '',
    landSurroundings: (landSurroundings) ? previousLandSurroundings : null,
    landSurroundingsText: landSurroundingsText || '',
    buildingNearbyHazard: '',
    landShape: getOption(landShapeOptions, landShape),
    landDescription: '',

    fileType2Switch: false,
    fileType8Switch: false,

    ...rest
  };

  const fileTypeIds = {
    environmentLocatorMap: T.ENVIRONMENT_LOCATOR_MAP,
    idfLocatingPlot: T.IDF_LOCATING_PLOT,
  };

  const environmentLocatorMapFileUploaded = isFileUploaded({fileList: filesListData, fileTypeID: T.ENVIRONMENT_LOCATOR_MAP});
  const IDFLocatingPlotFileUploaded = isFileUploaded({fileList: filesListData, fileTypeID: T.IDF_LOCATING_PLOT});

  const {params: {shumaId, formId: stepId}} = useRouteMatch(P.INSPECTION_AND_FORM_IDS);

  const validate = values => {
    const {
      areaLimits,
      areaUniqueFeatures,
      propertyLocationDesc,
      adjacentStreets,
      landSurroundings,
      buildingNearbyHazard,
      landShape,
      streetLocation,
      landDescription,
      neighborhoodName,
      buildType,
      buildPeriod,
      facing,
      streetType,
      streetTraffic,
      fileType2Switch,
      fileType8Switch,
      streetMainUsage,
      ...rest
    } = values;

    return checkRequiredValues(rest, {})
  };

  const changedValue = (value) => {
    if (value) {
      return value.map(item => item.value).join()
    }
  };

  const getAddress = () => {
    const {cityName, streetName, houseNum} = stepData;

    return (Boolean(cityName) && Boolean(streetName) && Boolean(houseNum))
      ? `${houseNum} ${streetName} ${cityName}`
      : null;
  };

  const submitWithoutFormReinitialization = values => {
    const {
      buildType,
      buildPeriod,
      facing,
      streetLocation,
      streetType,
      streetTraffic,
      parkingIssues,
      publicTransport,
      parkingIssuesTimes,
      areaLimits,
      areaUniqueFeatures,
      envDevelopmentLevel,
      streetMainUsage,
      adjacentStreets,
      neighborhoodName,
      propertyLocationDesc,
      landSurroundings,
      landSurroundingsText,
      buildingNearbyHazard,
      landShape,
      landDescription,
    } = normalizeFormValues(values);
    const data = {
      shumaID: shumaId,
      shumaReason: 1,
      buildType,
      buildPeriod,
      facing,
      streetLocation,
      streetType,
      streetTraffic,
      parkingIssues,
      publicTransport,
      parkingIssuesTimes,
      areaLimits,
      areaUniqueFeatures,
      envDevelopmentLevel,
      streetMainUsage,
      adjacentStreets,
      neighborhoodName,
      propertyLocationDesc,
      landSurroundings: changedValue(landSurroundings),
      landSurroundingsText,
      buildingNearbyHazard,
      landShape,
      landDescription,
    };

    setReinit(false);  //https://github.com/formium/formik/issues/2369
    return submitStep({step: 4, data})
      .then(() => setReinit(true))
  };

  const handleDataSubmit = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleSubmitForSharedFiles = formikProps => {
    Promise.resolve()
      .then(() => {
        submitWithoutFormReinitialization(formikProps.values)
      })
      .then(() => getShumaStepStatus({shumaId, resetValues: false}))
  };

  const handleFileUpload = formikProps => fileTypeId => (file, payload) => {
    setShowUploadingFilePopUp(true);
    submitFiles(fileTypeId)(file, payload)
      .then(() => uploadingProgress(setProgress))
      .then(() => submitWithoutFormReinitialization(formikProps.values))
      .then(() => getShumaStepStatus({shumaId, resetValues: false}))
      .finally(() => {
        setShowUploadingFilePopUp(false);
        getFileList({shumaId});
        getStepData({shumaId, stepId});
      })
  };

  useEffect(() => {
    getFileList({shumaId});
  }, []);

  const submitFiles = fileTypeId => (fileToSave, payload) => {
    return uploadFile({shumaId, filename: fileToSave.name, fileTypeId, fileToSave}, payload);
  };

  const getFileSelectOptions = formikProps => {
    const filteredSharedFileID2 = filterFileListByFileTypeId(fileList, fileTypeIds.environmentLocatorMap);
    const filteredSharedFileID8 = filterFileListByFileTypeId(fileList, fileTypeIds.idfLocatingPlot);

    const onSubmit = () => handleSubmitForSharedFiles(formikProps);

    return {
      environmentLocatorMap: {
        type: getSharedAndPrivateFiles(filteredSharedFileID2),
        fileTypeID: fileTypeIds.environmentLocatorMap,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.environmentLocatorMap),
      },
      idfLocatingPlot: {
        type: getSharedAndPrivateFiles(filteredSharedFileID8),
        fileTypeID: fileTypeIds.idfLocatingPlot,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.idfLocatingPlot),
      },
    };
  };

  const handleDataSubmitForPreview = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleDataSubmitForSave = (values, shumaId, dirty) => {
    if (dirty) {
      setLoading(true);
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message))
        .finally(() => setLoading(false));
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={() => {
      }}
      enableReinitialize={reinit}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {(formikProps) => (
        <>
          <SilentSubmitHandler
            onSubmit={handleDataSubmit}
            values={formikProps.values}
            shumaId={shumaId}
            dirty={formikProps.dirty}
          />
          <ButtonSubmitForm
            onClick={() => {
              handleDataSubmitForSave(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
            disabled={!formikProps.dirty}
          />
          <ModalPreviewDocument
            submitOnOpen={() => {
              handleDataSubmitForPreview(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
          />
          {loading ? <SpinnerContainer/> : (
            <Form_4View
              {...formikProps}
              shumaID={shumaId}
              stepID={stepId}
              prevPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 13)}
              nextPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 3)}
              options={{
                buildTypeOptions,
                buildPeriodOptions,
                facingOptions,
                streetLocationOptions,
                streetTypeOptions,
                streetTrafficOptions,
                parkingIssuesTimesOptions,
                landSurroundingsOptions,
                landShapeOptions
              }}
              fileUploaded={{
                environmentLocatorMapFileUploaded,
                IDFLocatingPlotFileUploaded,
              }}
              onFileSelected={handleFileUpload(formikProps)}
              fileTypeIds={fileTypeIds}
              showUploadingFilePopUp={showUploadingFilePopUp}
              progress={progress}
              mapPosition={getAddress()}
              fileSelectOptions={getFileSelectOptions(formikProps)}
              stepData={stepData}
              filesListData={filesListData ?? []}
            />
          )}
        </>
      )}
    </Formik>
  )
};

export default Form_4;